import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loading } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const convos = useQuery({ queryKey: ["conversations"], queryFn: () => api("/conversations") });
  const convo = (convos.data ?? []).find((c) => c.id === id);
  const messages = useQuery({ queryKey: ["messages", id], queryFn: () => api(`/conversations/${id}/messages`), refetchInterval: 4000 });

  const send = useMutation({
    mutationFn: (t) => api(`/conversations/${id}/messages`, { method: "POST", body: { text: t } }),
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["messages", id] }); qc.invalidateQueries({ queryKey: ["conversations"] }); },
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.data?.length]);
  const submit = (e) => { e.preventDefault(); if (text.trim()) send.mutate(text.trim()); };

  return (
    <div data-testid="chat-screen" className="max-w-2xl flex flex-col" style={{ height: "calc(100vh - 8rem)" }}>
      <PageHeader title={convo?.other?.name || "Chat"} onBack={() => navigate("/messages")} />
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {messages.isLoading ? <Loading /> : (messages.data ?? []).map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[75%] px-4 py-2 rounded-xl2", mine ? "bg-brand text-white rounded-br-sm" : "bg-surface2 text-ink rounded-bl-sm")}>
                <div className="font-body text-[15px] whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 pt-3">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="flex-1 h-12 px-4 rounded-pill border border-line bg-surface2 font-body text-[15px] outline-none focus:border-brand" />
        <button type="submit" data-testid="chat-send" className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Send size={18} /></button>
      </form>
    </div>
  );
}
